/* =============================================================================
   Legere Parts — página de produto
   Troca de variação, recomendações e compartilhamento.

   A troca de variação re-renderiza a coluna de informações pelo servidor. Custa
   uma requisição, mas garante que preço, parcelamento, Pix, SKU e estoque venham
   sempre do Liquid — sem reimplementar essas regras no JavaScript e correr o
   risco de as duas versões divergirem.
   ========================================================================== */
(function () {
  'use strict';

  const T = window.LegereTheme;
  const { init, toast } = T.utils;

  /* --- Troca de variação ---------------------------------------------------- */

  function getVariants(section) {
    const script = section.querySelector('[data-product-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (_) {
      return [];
    }
  }

  function selectedOptions(section) {
    const inputs = Array.from(section.querySelectorAll('[data-option-position]:checked'));
    return inputs
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);
  }

  function matchVariant(variants, options) {
    return variants.find((variant) =>
      variant.options.every((value, index) => value === options[index])
    );
  }

  /** Atualiza a foto principal quando a variação tem imagem própria. */
  function updateGallery(section, variant) {
    if (!variant || !variant.featured_image) return;
    const main = section.querySelector('[data-gallery-main]');
    if (!main) return;
    main.src = variant.featured_image.src;
    main.removeAttribute('srcset');
    main.alt = variant.featured_image.alt || main.alt;
    main.dataset.zoomSrc = variant.featured_image.src;
  }

  async function renderVariant(section, variant) {
    const info = section.querySelector('[data-product-info]');
    const sectionId = section.dataset.sectionId;
    const productUrl = info && info.dataset.productUrl;
    if (!info || !sectionId || !productUrl) return;

    // Na visualização rápida não existe seção para pedir de volta, e o cliente
    // está em outra página — buscamos o próprio fragmento e não mexemos na URL.
    const isQuickView = sectionId === 'quick-view';

    if (!isQuickView) {
      // Mantém a URL compartilhável apontando para a variação escolhida.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    const endpoint = isQuickView
      ? `${productUrl}?variant=${variant.id}&view=quick-view`
      : `${productUrl}?variant=${variant.id}&section_id=${sectionId}`;

    // Guarda qual opção tinha o foco para devolvê-lo após o redesenho.
    const active = document.activeElement;
    const activeName = active && active.name;
    const activeValue = active && active.value;

    try {
      const res = await fetch(endpoint);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('[data-product-info]');
      if (!next) return;

      info.innerHTML = next.innerHTML;
      init(info);

      // A barra fixa vive fora da coluna re-renderizada: copiamos o preço novo.
      const freshPrice = info.querySelector('[data-price-target]');
      const stickyPrice = document.querySelector('.sticky-atc [data-price-target]');
      if (freshPrice && stickyPrice) stickyPrice.innerHTML = freshPrice.innerHTML;

      if (activeName) {
        const restore = info.querySelector(`[name="${activeName}"][value="${CSS.escape(activeValue)}"]`);
        if (restore) restore.focus();
      }
    } catch (_) {
      // Se o redesenho falhar, ao menos o id enviado ao carrinho fica correto.
      info.querySelectorAll('[data-variant-input]').forEach((el) => {
        el.value = variant.id;
      });
    }

    document.querySelectorAll('.sticky-atc [data-variant-input]').forEach((el) => {
      el.value = variant.id;
    });

    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));
  }

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-option-position]');
    if (!input) return;

    const section = input.closest('[data-section-id]');
    if (!section) return;

    const variants = getVariants(section);
    const variant = matchVariant(variants, selectedOptions(section));

    if (!variant) {
      const button = section.querySelector('[data-add-button]');
      if (button) {
        button.disabled = true;
        button.textContent = T.strings.soldOut;
      }
      return;
    }

    updateGallery(section, variant);
    renderVariant(section, variant);
  });

  /* --- Recomendações --------------------------------------------------------- */

  async function loadRecommendations(slot) {
    if (slot.dataset.loaded) return;
    slot.dataset.loaded = 'true';

    try {
      const res = await fetch(slot.dataset.url);
      if (!res.ok) return;
      const html = await res.text();

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-section-content]');
      if (!content) return;

      slot.innerHTML = content.outerHTML;
      init(slot);
    } catch (_) {
      /* sem recomendações a página continua completa */
    }
  }

  function observeRecommendations(scope) {
    const slots = (scope || document).querySelectorAll('[data-recommendations]');
    if (!slots.length) return;

    if (!('IntersectionObserver' in window)) {
      slots.forEach(loadRecommendations);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadRecommendations(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '400px' }
    );

    slots.forEach((slot) => observer.observe(slot));
  }

  /* --- Compartilhar ---------------------------------------------------------- */

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-share]');
    if (!trigger) return;
    event.preventDefault();

    const url = trigger.href;
    const title = document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (_) {
        return; // o usuário cancelou; não é erro
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      toast(T.strings.linkCopied);
    } catch (_) {
      window.open(url, '_blank', 'noopener');
    }
  });

  /* --- Início ---------------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded', () => observeRecommendations(document));
  document.addEventListener('shopify:section:load', (event) => observeRecommendations(event.target));
})();
